import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import FoldersThreeDotMenu from '../FoldersThreeDotMenu';
import { ASSETS } from '../../../utils/config';

const StatusBox = styled('div')(({ isPublic }) => ({
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '2px 10px',
    backgroundColor: isPublic ? '#E8F5E9' : '#F5F5F5',
    borderRadius: '20px',
    justifyContent: 'center',
    width: 'fit-content'
}));

const StatusText = styled(Typography)(({ isPublic }) => ({
    fontSize: '12px',
    lineHeight: '18px',
    paddingLeft: '5px',
    color: isPublic ? '#2E7D32' : '#757575',
    fontWeight: 500
}));

const columnHeaders = [
    { id: 'name', label: 'Name', width: '50%', paddingLeft: true },
    { id: 'visibility', label: 'Visibility', width: '25%' },
    { id: 'lastModified', label: 'Last Modified', width: '25%' }
];

const ListView = ({
    items = [],
    onItemDoubleClick,
    onItemSettingsClick,
    onItemVisibilityChange,
    onItemDeleteClick,
    onItemMoveClick,
    isUserProfile = false
}) => {
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const options = {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };
        return date.toLocaleDateString('en-US', options);
    };

    const getVisibilityIcon = (visibility) => {
        return visibility === 1
            ? ASSETS.segmentEditor.PublicFolder
            : ASSETS.segmentEditor.PrivateFolder;
    };

    const getVisibilityStatusIcon = (visibility) => {
        return visibility === 1
            ? ASSETS.segmentEditor.PublicWheel
            : ASSETS.segmentEditor.PrivateWheel
    }


    const getVisibilityText = (visibility) => {
        return visibility === 1 ? 'Public' : 'Private';
    };

    return (
        <TableContainer sx={{ overflow: 'hidden', mt: 3 }}>
            <Table>
                <TableHead sx={{
                    backgroundColor: '#F8F9FA',
                    height: 40,
                    borderBottom: '3px solid #6200EE'
                }}>
                    <TableRow>
                        {columnHeaders.map((column) => (
                            <TableCell
                                key={column.id}
                                padding="none"
                                sx={{
                                    width: column.width,
                                    paddingLeft: column.paddingLeft ? 2 : 0,
                                    userSelect: 'none'
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: 700,
                                        color: '#123051'
                                    }}
                                >
                                    {column.label}
                                </Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item) => (
                        <TableRow
                            key={item.id}
                            sx={{
                                height: '60px',
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: '#F8F9FA'
                                }
                            }}
                            onDoubleClick={() => onItemDoubleClick?.(item)}
                        >
                            <TableCell padding="none">
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    pl: 2
                                }}>
                                    <img
                                        src={getVisibilityIcon(item.visibility)}
                                        width={24}
                                        height={24}
                                        style={{ marginRight: 10 }}
                                        alt="Folder Icon"
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: 14,
                                            color: '#123051',
                                            fontWeight: 500
                                        }}
                                    >
                                        {item.name}
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell padding="none">
                                <StatusBox isPublic={item.visibility === 1}>
                                    <img
                                        src={getVisibilityStatusIcon(item.visibility)}
                                        width={20}
                                        height={20}
                                        alt="Status Icon"
                                    />
                                    <StatusText isPublic={item.visibility === 1}>
                                        {getVisibilityText(item.visibility)}
                                    </StatusText>
                                </StatusBox>
                            </TableCell>
                            <TableCell padding="none">
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    pr: 2
                                }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {formatDate(item.updated_at)}
                                    </Typography>
                                    {isUserProfile && (
                                        <FoldersThreeDotMenu
                                            folder={item}
                                            onSettingsClick={onItemSettingsClick}
                                            onMoveClick={onItemMoveClick}
                                            onVisibilityChange={onItemVisibilityChange}
                                            onDeleteClick={onItemDeleteClick}
                                        />
                                    )}
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                    {items.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                <Typography variant="body1" color="textSecondary">
                                    No folders to display
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ListView;